import { EmployeeAvatar, EmployeeAvatarProps } from "@/components/employee/EmployeeAvatar.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { LinkTo, Stack, Text, Tooltip, TooltipContent, TooltipTrigger } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export interface EmployeeAvatarWithTooltipProps extends Omit<EmployeeAvatarProps, "employee"> {
  employees?: ApiEmployeeShort[];
  employee?: ApiEmployeeShort;
  enableLink?: boolean;
  maxVisible?: number;
  totalCount?: number;
}

export const EmployeeAvatarWithTooltip = ({
  employees,
  employee,
  enableLink = false,
  maxVisible,
  totalCount,
  ...avatarProps
}: EmployeeAvatarWithTooltipProps) => {
  const { renderEmployeeName } = useFormatter();
  const { t } = useTranslation();

  if (!employee && !employees) return null;

  let employeesList: ApiEmployeeShort[] = [];
  if (employees) employeesList = employees;
  if (employee) employeesList = [employee];

  const renderAvatar = (emp: ApiEmployeeShort) => (
    <Tooltip key={emp.id}>
      <TooltipTrigger>
        <LinkTo
          to={`/employees/${emp.id}`}
          target="_blank"
          disabled={emp.is_archived || !enableLink}
          className="no-underline"
        >
          <EmployeeAvatar employee={emp} {...avatarProps} />
        </LinkTo>
      </TooltipTrigger>
      <TooltipContent>
        <Text>{renderEmployeeName(emp)}</Text>
      </TooltipContent>
    </Tooltip>
  );

  if (maxVisible !== undefined) {
    const visible = employeesList.slice(0, maxVisible);
    const remaining = Math.max((totalCount ?? employeesList.length) - visible.length, 0);

    return (
      <Stack direction="horizontal" gap="xs" items="center">
        {visible.map(renderAvatar)}
        {remaining > 0 && (
          <Text className="text-secondary">{` +${remaining} ${t("common:others")}`}</Text>
        )}
      </Stack>
    );
  }

  if (employeesList.length <= 2) {
    return (
      <Stack direction="horizontal" gap="xs">
        {employeesList.map(renderAvatar)}
      </Stack>
    );
  }

  return (
    <Stack direction="horizontal" gap="xs">
      {renderAvatar(employeesList[0])}
      <Tooltip>
        <TooltipTrigger>
          <Text className="text-secondary">{`+${t("plurals:plural_others", { count: employeesList.length - 1 })}`}</Text>
        </TooltipTrigger>
        <TooltipContent>
          <Stack>
            {employeesList.slice(1).map((emp) => (
              <LinkTo
                key={emp.id}
                to={`/employees/${emp.id}`}
                target="_blank"
                disabled={emp.is_archived || !enableLink}
              >
                <Text>{renderEmployeeName(emp)}</Text>
              </LinkTo>
            ))}
          </Stack>
        </TooltipContent>
      </Tooltip>
    </Stack>
  );
};
